import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import SettingsForm from './SettingsForm';
import platformSettingsService from '../../services/platformSettingsService';
import type { PlatformSettings, PlatformWebhookStatus } from '../../types';

const errorText = (err: any, fallback: string): string => err?.response?.data?.error ?? err?.message ?? fallback;

/** PayPal-Webhook: ID speichern, Registrierung für die aktuelle Umgebungs-URL prüfen oder anlegen. */
const PayPalTab: React.FC<{ data: PlatformSettings; onChange: (s: PlatformSettings) => void }> = ({ data, onChange }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [status, setStatus] = useState<PlatformWebhookStatus | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = async (action: 'check' | 'register') => {
        setBusy(true);
        setError(null);
        try {
            if (action === 'register') {
                const result = await platformSettingsService.registerWebhook();
                notify(translate(result.created ? 'platformSettings.paypal.created' : 'platformSettings.paypal.exists'), { type: 'success' });
                onChange(await platformSettingsService.get());
            }
            setStatus(await platformSettingsService.webhookStatus());
        } catch (err: any) {
            setError(errorText(err, translate('platformSettings.paypal.error')));
        } finally {
            setBusy(false);
        }
    };

    const p = data.paypal;

    return (
        <Box sx={{ display: 'grid', gap: 3 }}>
            <Paper sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                    <Chip size="small" variant="outlined" data-testid="paypal-env" label={translate(`platformSettings.paypal.env.${p.env}`)} />
                    <Chip size="small" variant="outlined" data-testid="paypal-credentials" label={translate(p.credentialsConfigured ? 'platformSettings.paypal.credentialsOk' : 'platformSettings.paypal.credentialsMissing')} />
                </Box>
                <Typography variant="body2" sx={{ mb: 0.5, color: 'var(--oa-text-2)' }}>{translate('platformSettings.paypal.url')}</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all', mb: 2 }} data-testid="paypal-url">{p.webhookUrl}</Typography>
                {!p.credentialsConfigured && <Alert severity="warning" sx={{ mb: 2 }}>{translate('platformSettings.paypal.credentialsHint')}</Alert>}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button variant="outlined" disabled={busy || !p.credentialsConfigured} onClick={() => run('check')} data-testid="paypal-check">{translate('platformSettings.paypal.check')}</Button>
                    <Button variant="contained" disabled={busy || !p.credentialsConfigured} onClick={() => run('register')} data-testid="paypal-register">{translate('platformSettings.paypal.register')}</Button>
                </Box>
                {error && <Alert severity="error" sx={{ mt: 2 }} data-testid="paypal-error">{error}</Alert>}
                {status && (
                    <Alert severity={status.registered && status.matchesStored && status.missingEvents.length === 0 ? 'success' : 'warning'} sx={{ mt: 2 }} data-testid="paypal-status">
                        {status.registered
                            ? translate(status.matchesStored ? 'platformSettings.paypal.registeredOk' : 'platformSettings.paypal.registeredMismatch', { id: status.webhookId ?? '' })
                            : translate('platformSettings.paypal.notRegistered')}
                        {status.registered && status.missingEvents.length > 0 && (
                            <Box component="span" sx={{ display: 'block' }}>{translate('platformSettings.paypal.missingEvents', { events: status.missingEvents.join(', ') })}</Box>
                        )}
                    </Alert>
                )}
            </Paper>
            <Paper sx={{ p: 2.5 }}>
                <SettingsForm
                    key={p.webhookId.value + p.webhookId.source}
                    group="paypal"
                    labelPrefix="platformSettings.paypal"
                    defs={[{ key: 'webhook_id' }]}
                    data={{ webhook_id: p.webhookId }}
                    onSave={async (values) => onChange(await platformSettingsService.update({ paypal: values }))}
                />
            </Paper>
        </Box>
    );
};

export default PayPalTab;
